/**
 * Shared feedback-button icon presets.
 *
 * The widget button's `iconUrl` field is overloaded to select one of three
 * icon sources (see {@link resolveIcon}):
 *  - a **preset** — `iconUrl` is `"preset:<name>"` where `<name>` is a key of
 *    {@link ICON_PRESETS}; the widget renders the corresponding inline SVG;
 *  - a **custom url** — `iconUrl` is an `http(s)://…` image URL; the widget
 *    renders an `<img>`;
 *  - the **default** — `iconUrl` is empty/null or an unrecognized value; the
 *    widget renders its bundled default icon.
 *
 * Presets are shared here (rather than duplicated in the widget + admin
 * portal) so the admin picker and the widget renderer agree on the exact same
 * set. Each value is the INNER markup of a `24×24` `viewBox` SVG, drawn with
 * `currentColor` so the widget can color it via `iconColor`.
 */

/** Prefix marking an `iconUrl` value as a preset selector (`"preset:bug"`). */
export const PRESET_PREFIX = 'preset:';

/**
 * Preset name → inner SVG markup (path/circle/line elements) on a `24×24`
 * viewBox. Stroke-based, `strokeWidth ~2`, round caps, using `currentColor`
 * so the drawn icon inherits the widget's configured icon color.
 *
 * These strings are trusted, author-controlled constants — never user input —
 * which is what makes it safe for the widget to inject them as SVG innerHTML.
 */
export const ICON_PRESETS = {
  feedback:
    '<path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.9-5.6A8.5 8.5 0 0 1 4 11.5 8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  bug:
    '<path d="M9 5a3 3 0 0 1 6 0" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><rect x="8" y="7" width="8" height="11" rx="4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="12" y1="10" x2="12" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="8" y1="10" x2="4" y2="8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="8" y1="13" x2="3" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="8" y1="16" x2="4" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="16" y1="10" x2="20" y2="8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="16" y1="13" x2="21" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="16" y1="16" x2="20" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  info:
    '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><line x1="12" y1="11" x2="12" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  idea:
    '<path d="M9 18h6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 21h4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 3a6 6 0 0 0-4 10.5c.7.6 1 1.2 1 2V16h6v-.5c0-.8.3-1.4 1-2A6 6 0 0 0 12 3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  // D v3, the same 24-grid paths as HELP_PATHS in portal-ui Bell.tsx, at the
  // launcher's 20px. Filled, no circle and no frame: a frame shrinks the mark
  // to about 11px. Copied here rather than imported from App Grid. That
  // package's shared export is a separate PR, and this one does not wait on it.
  question:
    '<path d="M7.2 2.4 L7.2 6.062 L11.57 9.122 L7.2 12.182 L7.2 15.6 L10.8 15.6 L10.8 13.323 L16.8 9.122 Z" fill="currentColor"/><path d="M7.2 19.2 H10.8 V22.8 H7.2 Z" fill="currentColor"/>',
  warning:
    '<path d="M12 3 2 20h20L12 3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="12" y1="10" x2="12" y2="14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="12" y1="17" x2="12" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  star:
    '<path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.3l6.1-.7L12 3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  heart:
    '<path d="M12 20s-7-4.5-9.2-8.5A4.7 4.7 0 0 1 12 6a4.7 4.7 0 0 1 9.2 5.5C19 15.5 12 20 12 20z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  sparkle:
    '<path d="M12 3c.5 3.5 2 5 5.5 5.5C14 9 12.5 10.5 12 14c-.5-3.5-2-5-5.5-5.5C10 8 11.5 6.5 12 3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M18 14c.3 1.8 1 2.5 2.8 2.8-1.8.3-2.5 1-2.8 2.8-.3-1.8-1-2.5-2.8-2.8 1.8-.3 2.5-1 2.8-2.8z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
  megaphone:
    '<path d="M3 11l18-5v12L3 14v-3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
} as const satisfies Record<string, string>;

/** A valid preset name — a key of {@link ICON_PRESETS}. */
export type IconPreset = keyof typeof ICON_PRESETS;

/** Discriminated result of {@link resolveIcon}. */
export type ResolvedIcon =
  | { kind: 'preset'; name: IconPreset }
  | { kind: 'url'; url: string }
  | { kind: 'default' };

/**
 * Interpret an application's `iconUrl` value into which icon source to render.
 *
 *  - `"preset:<name>"` with a known `<name>` → `{ kind: 'preset', name }`
 *  - an `http://` / `https://` URL → `{ kind: 'url', url }`
 *  - anything else (empty, null, unknown preset, non-http string) →
 *    `{ kind: 'default' }`
 *
 * Backward compatible: existing values are either http(s) image URLs or
 * empty/null, so they resolve to `url` / `default` exactly as before.
 */
export function resolveIcon(iconUrl: string | null | undefined): ResolvedIcon {
  if (!iconUrl) {
    return { kind: 'default' };
  }

  if (iconUrl.startsWith(PRESET_PREFIX)) {
    const name = iconUrl.slice(PRESET_PREFIX.length);
    if (Object.prototype.hasOwnProperty.call(ICON_PRESETS, name)) {
      return { kind: 'preset', name: name as IconPreset };
    }
    return { kind: 'default' };
  }

  if (iconUrl.startsWith('http://') || iconUrl.startsWith('https://')) {
    return { kind: 'url', url: iconUrl };
  }

  return { kind: 'default' };
}
